import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Spinner, Pagination } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
    fetchNoPeanutPolicies,
    selectPolicies,
    selectMeta,
    selectLoading,
    selectError
} from '../../store/noPeanutPolicy/noPeanutPolicySlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const NoPeanutPolicyListComp = ({ data, viewMode }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const policies = useSelector(selectPolicies) || [];
    const meta = useSelector(selectMeta);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState(null);

    useEffect(() => {
        dispatch(fetchNoPeanutPolicies({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
    }, [dispatch, currentPage]);
    // Always call hooks first
    // In profile view, always use the passed-in data prop (already filtered by parent)
    if (viewMode === 'profile') {
        if (!Array.isArray(data) || data.length === 0) {
            // Show nothing if no active data (parent will show form)
            return null;
        }
        // Render only the passed-in data
        return (
            <Container>
                <Row>
                    {data.map((policy) => (
                        <Col md="12" key={policy.id} className="mb-3">
                            <Card className="border">
                                <Card.Body>
                                    <Row className="align-items-center">
                                        <Col md="6">
                                            <h6 className="mb-2">
                                                Policy #{policy.id}
                                                <Badge bg="success" className="ms-2">Active</Badge>
                                            </h6>
                                            <p className="mb-1 text-muted">
                                                <strong>Submitted:</strong> {policy.created_at}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Description:</strong> {policy.description || 'N/A'}
                                            </p>
                                            {policy.parent_signature && (
                                                <div className="mt-2">
                                                    <strong>Parent Signature:</strong>
                                                    <div className="mt-1">
                                                        <img
                                                            src={policy.parent_signature}
                                                            alt="Parent Signature"
                                                            style={{ maxWidth: '200px', maxHeight: '80px', border: '1px solid #ddd', borderRadius: '4px', padding: '4px' }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        <Col md="6" className="d-flex gap-2 justify-content-end">
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() => setShowModal(true)}
                                            >
                                                <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>visibility</i>
                                                View
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => navigate(`/dashboard/no-peanut-policy/edit/${policy.id}`)}
                                            >
                                                <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>edit</i>
                                                Edit
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>
        );
    }
    // Add handlePageChange for pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleViewDetails = (policy) => {
        setSelectedPolicy(policy);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPolicy(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime()) || dateString.includes('Invalid') || dateString.includes('1899-11-29')) {
            return 'N/A';
        }
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const renderPagination = () => {
        if (!meta || meta.last_page <= 1) return null;

        const items = [];
        for (let number = 1; number <= meta.last_page; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <Pagination className="justify-content-center mt-3">
                <Pagination.Prev
                    key="prev"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                />
                {items}
                <Pagination.Next
                    key="next"
                    disabled={currentPage === meta.last_page}
                    onClick={() => handlePageChange(currentPage + 1)}
                />
            </Pagination>
        );
    };

    if (loading && (!policies || policies.length === 0)) {
        return (
            <Container>
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted mb-0">Loading policies...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <Card.Body className="text-center py-5">
                        <i className="icon material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
                        <p className="text-danger mt-2 mb-3">{error}</p>
                        <Button variant="primary" onClick={() => dispatch(fetchNoPeanutPolicies({ page: 1, limit: 10, student_id: getEffectiveStudentId(profileData) }))}>
                            Retry
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            {/* Scoped styles for this screen's cards - now full-width
                (one per row, see the Col below) rather than a multi-column
                grid, so there's room to show the description and signature
                without clamping/shrinking them. */}
            <style>{`
                .peanut-policy-card {
                    border: 1px solid #eef0f3;
                    border-radius: 12px;
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .peanut-policy-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08) !important;
                }
                .peanut-policy-signature-box {
                    height: 84px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px dashed #dcdfe3;
                    border-radius: 8px;
                    overflow: hidden;
                }
            `}</style>
            {!Array.isArray(policies) || policies.length === 0 ? (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <Card.Body className="text-center py-5">
                        <i className="icon material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>
                            description
                        </i>
                        <p className="mt-3 text-muted">No policies found. Create your first policy.</p>
                        <Link to="/dashboard/no-peanut-policy/add">
                            <Button variant="primary">Create Your First Policy</Button>
                        </Link>
                    </Card.Body>
                </Card>
            ) : (
                <Row className="g-3">
                    {policies.map((policy) => (
                        <Col xs="12" key={policy.id}>
                            <Card className="peanut-policy-card shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <h6 className="mb-0 fw-bold text-dark">Policy #{policy.id}</h6>
                                            <Badge
                                                bg={policy.status === 1 ? "success" : "secondary"}
                                                className="rounded-pill"
                                                style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', lineHeight: 1.3 }}
                                            >
                                                {policy.status === 1 ? "Active" : "Inactive"}
                                            </Badge>
                                        </div>
                                        <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(policy)}
                                                >
                                                    <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>visibility</i>
                                                    View
                                                </Button>
                                                {policy.status === 1 && (
                                                    <Link to={`/dashboard/no-peanut-policy/edit/${policy.id}`}>
                                                        <Button variant="primary" size="sm">
                                                            <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>edit</i>
                                                            Edit
                                                        </Button>
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Row className="g-4 align-items-start">
                                        <Col md="3">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Submitted
                                            </div>
                                            <div style={{ fontSize: '14px' }}>
                                                <i className="icon material-symbols-outlined me-1" style={{ fontSize: '14px', verticalAlign: 'middle' }}>calendar_today</i>
                                                {formatDate(policy.created_at)}
                                            </div>
                                        </Col>
                                        <Col md="5">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Description
                                            </div>
                                            <div style={{ fontSize: '14px' }}>
                                                {policy.description || <span className="text-muted fst-italic">No description provided</span>}
                                            </div>
                                        </Col>
                                        <Col md="4">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                Parent Signature
                                            </div>
                                            <div className="peanut-policy-signature-box">
                                                {policy.parent_signature ? (
                                                    <img
                                                        src={policy.parent_signature}
                                                        alt="Parent Signature"
                                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <span className="text-muted" style={{ fontSize: '12px' }}>No signature</span>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {renderPagination()}

            {/* View Details Modal - size="xl" (was "lg") so the full-width
                sections below actually use the available space. */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        No Peanut Policy #{selectedPolicy?.id} Details
                        {selectedPolicy && (
                            <Badge bg={selectedPolicy.status === 1 ? "success" : "secondary"} className="rounded-pill">
                                {selectedPolicy.status === 1 ? "Active" : "Inactive"}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {selectedPolicy && (
                        <div>
                            {/* Policy Information */}
                            <div className="mb-4 p-3" style={{ background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#dc3545' }}>
                                        <i className="icon material-symbols-outlined" style={{ fontSize: '18px' }}>no_food</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Policy Information</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="4">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Submitted Date</div>
                                        <div style={{ fontSize: '14px' }}>{formatDate(selectedPolicy.created_at)}</div>
                                    </Col>
                                    <Col md="8">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Description</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPolicy.description || 'N/A'}</div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Signature */}
                            <div>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                                        <i className="icon material-symbols-outlined" style={{ fontSize: '18px' }}>draw</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Parent Signature</h6>
                                </div>
                                {selectedPolicy.parent_signature ? (
                                    <>
                                        <div className="border rounded p-2 bg-light mb-2" style={{ maxWidth: '400px' }}>
                                            <img
                                                src={selectedPolicy.parent_signature}
                                                alt="Parent Signature"
                                                style={{ maxWidth: '100%', maxHeight: '150px', display: 'block' }}
                                            />
                                        </div>
                                        <small className="text-muted">Date: {formatDate(selectedPolicy.parent_signature_date)}</small>
                                    </>
                                ) : (
                                    <p className="text-muted">No signature available</p>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    {selectedPolicy?.status === 1 && (
                        <Link to={`/dashboard/no-peanut-policy/edit/${selectedPolicy.id}`}>
                            <Button variant="primary" onClick={handleCloseModal}>
                                <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>edit</i>
                                Edit
                            </Button>
                        </Link>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};
export default NoPeanutPolicyListComp;
